import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { RuleWarning } from '@/lib/ruleWarnings';

interface RuleWarningsDialogProps {
  /** The warnings the server refused with; the dialog is open while this holds any. */
  warnings: RuleWarning[] | null;
  /** Resend with the warnings acknowledged. */
  onConfirm: () => void;
  onCancel: () => void;
  /** True while the resubmission is in flight. */
  confirmDisabled?: boolean;
}

/**
 * C8's "continue despite warnings?": the advisory rules that fired, by message and rule key, before the
 * refused action is resent with them acknowledged. One surface for every caller — a command from an
 * actions menu, the save of a detail view whose entity declares updateWarnings.
 */
export function RuleWarningsDialog({ warnings, onConfirm, onCancel, confirmDisabled }: RuleWarningsDialogProps) {
  const { t } = useTranslation();

  return (
    <ConfirmDialog
      open={warnings !== null && warnings.length > 0}
      title={t('actions.warningsTitle', { defaultValue: 'Continue despite warnings?' })}
      message={t('actions.warningsMessage', { defaultValue: 'The action can still be completed. Continuing records that you accepted these warnings.' })}
      confirmLabel={t('actions.continueAnyway', { defaultValue: 'Continue anyway' })}
      cancelLabel={t('common.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={confirmDisabled}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <List dense disablePadding sx={{ mb: 1 }}>
        {(warnings ?? []).map((warning) => (
          <ListItem key={warning.ruleKey} disableGutters>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <WarningAmberRounded fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary={warning.message} secondary={warning.ruleKey} />
          </ListItem>
        ))}
      </List>
    </ConfirmDialog>
  );
}
